import { ensureRouteData, prefetchRouteData } from '@/app/query-client'
import {
  brokerDetailQueryOptions,
  brokerListQueryOptions,
  brokerReviewsQueryOptions,
} from '@/features/brokers/api/queries'
import { rememberLastBroker } from '@/features/brokers/lib/lastUsed'
import type { RouterContext } from '@/routes/layouts/root'
import { BROKER_DIRECTORY_QUERY } from '@/features/brokers/lib/brokerDirectory'
import { brokerReviewsSearchSchema, toBrokerReviewsQuery } from '@/screens/brokers/search'

/**
 * The `/brokers/$code/reviews` loader.
 *
 * MAIN, but keyed on the `page`/`pageSize` search params
 * (`brokerReviewsSearchSchema` IS `brokerReviewsQuerySchema`), so this awaits
 * only the ENTRY URL's page — a pager click afterwards is a search-only
 * change that reruns no loader, and `BrokerReviewsScreen` keeps reading it
 * with `useBrokerReviews`'s own `placeholderData: keepPreviousData`, not a
 * suspense hook (docs/route-loading.md, "Search params"). It feeds both
 * cards on the page (the rating summary and the review list).
 *
 * SECONDARY, started and not awaited, and only on a real navigation
 * (docs/route-loading.md, "Secondary reads"): the broker detail, read at
 * `'today'` exactly as `BrokerScreen` reads it for its own header — only
 * for the house's name in the title and the broker picker, which already
 * fall back to `brokerDisplayName(code, null)` while this is still out —
 * and the picker's list of houses, the same key By broker fills. Nothing
 * here blocks the page. Skipped on a hover-intent `preload`; the MAIN read
 * below still starts on hover.
 *
 * `BROKER_NOT_FOUND` resolves to `null` rather than throwing
 * (docs/route-loading.md, "Not-found answers" — see
 * `brokerReviewsQueryOptions`'s own comment), so this loader settles
 * normally even for a bad code, and
 * `BrokerReviewsScreen` keeps its own `data === null` branch.
 *
 * A house whose reviews resolve becomes the member's last-used one, as on
 * By broker. This page used to leave `lastUsed` alone, as a sub-page of the
 * broker's own; since the pages revamp (2026-10-27) Reviews is a tab of the
 * section strip with its own broker picker, and that tab's link is the
 * param-less `/brokers/reviews`, which resolves through `lastUsed`. Without
 * the write, clicking the lit Reviews tab — or By broker beside it — would
 * leave the house just picked here for a different one.
 *
 * Its own module, as `app.guard.ts` is, so a test can mount it under a root
 * of its own: typed inside `createFileRoute`, a loader is bound to this
 * route's parent in the generated tree. It imports from `api/` and schema
 * modules only — route loaders are not code-split, so anything imported here
 * ships in the entry chunk.
 */
export async function loadBrokerReviews({
  context: { queryClient },
  params,
  location,
  preload,
}: {
  context: RouterContext
  params: { code: string }
  location: { search: unknown }
  preload: boolean
}): Promise<void> {
  if (!preload) {
    prefetchRouteData(queryClient, brokerDetailQueryOptions(params.code, 'today'))
    prefetchRouteData(queryClient, brokerListQueryOptions(BROKER_DIRECTORY_QUERY))
  }
  const search = brokerReviewsSearchSchema.parse(location.search)
  // Through `toBrokerReviewsQuery`, the same picker the screen's own
  // `useBrokerReviews` call goes through — see search.ts's own comment on why
  // `search` itself is not safe to hand to `brokerReviewsQueryOptions`.
  const result = await ensureRouteData(
    queryClient,
    brokerReviewsQueryOptions(params.code, toBrokerReviewsQuery(search)),
  )
  if (!preload && result !== null) rememberLastBroker(params.code)
}
